import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';

import {
  DurationFields,
  DurationRequirement,
  DurationSummary,
  WAIT_FOR_MINIMUM_MINUTES,
  getDurationMinutes,
  normalizeDuration,
  toDurationValues,
} from '../duration';

type WaitForData = {
  days: string;
  hours: string;
  minutes: string;
  description: string;
};

const ACTION_TYPE = ACTION_TYPES.wait_for_time;
const Form = createActionForm<WaitForData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();

  const days = (formStore.useFieldValue('days') ?? '') as string;
  const hours = (formStore.useFieldValue('hours') ?? '') as string;
  const minutes = (formStore.useFieldValue('minutes') ?? '') as string;
  const durationMinutes = getDurationMinutes({ days, hours, minutes });
  const isValid = durationMinutes >= WAIT_FOR_MINIMUM_MINUTES;

  const values = data?.values;
  const defaults = normalizeDuration({
    days: values?.days?.value,
    hours: values?.hours?.value,
    minutes: values?.minutes?.value,
  });
  const isDurationDirty = durationMinutes !== getDurationMinutes(defaults);
  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: WaitForData) => {
    const values = toDurationValues(data, WAIT_FOR_MINIMUM_MINUTES);
    if (!values) return false;
    return editMutation.mutateAsync({
      action: ACTION_TYPE,
      isAutomatic: true,
      values,
      description: data.description,
    });
  };

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <DescriptionFormField defaultValue={data?.description} />
            </HideContainer>
            <Sidebar.Separator />
            <Stack space={4}>
              <Text text={t('selectHowLong')} font="regular14" />
              <Text text={t('useWhen')} font="regular14" />
            </Stack>
            <Separator />
            <DurationFields form={Form} defaults={defaults} />
            <DurationSummary variant="wait" parts={{ days, hours, minutes }} />
            {!isValid && <DurationRequirement variant="wait" isDirty={isDurationDirty} />}
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading || !isValid}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    actionDetails: 'Action details',
    selectHowLong: 'Select for how long journey should wait before triggering the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
  },
};
